import { useEffect, useRef, useState } from "react";
import { Download, FileText, FolderOpen, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/components/Brand";
import { fmtDate } from "@/i18n";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";

const size = (bytes) => (bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

export default function Files({ lang, t, setData }) {
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  useEffect(() => { api.get("/files").then((r) => setFiles(r.data)).catch(() => setFiles([])); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error(t.fileLimit); return; }
    setBusy(true);
    const form = new FormData(); form.append("file", file);
    try {
      const r = await api.post("/files", form);
      setFiles((list) => [r.data, ...(list || [])]);
      setData((d) => ({ ...d, counts: { ...d.counts, files: (d.counts?.files || 0) + 1 } }));
      toast.success(t.saved);
    } catch (err) { toast.error(errorText(err, "Upload failed.")); } finally { setBusy(false); }
  };
  const download = async (f) => {
    try {
      const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(errorText(err, "Download failed.")); }
  };
  const remove = async (f) => {
    await api.delete(`/files/${f.id}`);
    setFiles((list) => list.filter((x) => x.id !== f.id));
    setData((d) => ({ ...d, counts: { ...d.counts, files: Math.max(0, (d.counts?.files || 1) - 1) } }));
  };

  return (
    <div className="section-page">
      <PageTitle eyebrow={t.eyebrows.files} title={t.files} sub={t.filesSub}>
        <input ref={input} type="file" hidden onChange={upload} data-testid="file-input" />
        <button className="primary-button" onClick={() => input.current.click()} disabled={busy} data-testid="upload-file-button"><Upload size={16} />{busy ? t.uploading : t.upload}</button>
      </PageTitle>
      <p className="muted small">{t.fileLimit}</p>
      <p className="source-note">{t.filesSource}</p>
      {files === null ? <p className="muted">{t.loading}</p> : files.length === 0 ? (
        <div className="empty-state" data-testid="files-empty"><div className="empty-icon"><FolderOpen size={22} /></div><p>{t.noFiles}</p><StrategyRoomCTA t={t} testId="files-strategy-room" /></div>
      ) : (
        <div className="list" data-testid="files-list">
          {files.map((f) => (
            <div className="list-item" key={f.id} data-testid={`file-${f.id}`}>
              <div className="list-row">
                <div className="file-meta"><span className="file-icon">{f.content_type?.startsWith("image/") ? <ImageIcon size={16} /> : <FileText size={16} />}</span><div><strong>{f.original_filename}</strong><small>{size(f.size)} · {fmtDate(f.created_at, lang)}</small></div></div>
                <div className="row-actions">
                  <button className="quiet-button" onClick={() => download(f)} data-testid={`file-download-${f.id}`}><Download size={14} /> {t.download}</button>
                  <button className="icon-button" onClick={() => remove(f)} aria-label={t.delete} data-testid={`file-delete-${f.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
